/**
 * Droits menu / accès direct pour les membres d’un compte formateur LyrraOne.
 * true (ou clé absente pour l’owner) = autorisé ; false = masqué et bloqué en URL.
 */

export type LoTeamPermissionGroup = {
  id: string;
  title: string;
  keys: readonly string[];
};

export const LO_TEAM_PERMISSION_KEYS = [
  // Pilotage
  'nav.dashboard',
  'nav.learner',
  'nav.analytics',
  // Activité
  'nav.flows',
  'nav.supports',
  'nav.learners',
  'nav.sales',
  // Marketing
  'nav.marketing',
  'nav.contacts',
  'nav.email',
  'nav.automations',
  'nav.funnels',
  'nav.bundles',
  'nav.payments',
  'nav.site',
  'nav.blog',
  'nav.creatorPage',
  'nav.crm',
  'nav.affiliate',
  'nav.promotions',
  'nav.booking',
  'nav.communities',
  'nav.webinars',
  'nav.physical',
  'nav.sms',
  // Médias
  'nav.videos',
  'nav.sounds',
  'nav.images',
  'nav.documents',
  'nav.mediaDeposits',
  // Aide & paramètres
  'nav.help',
  'nav.settings',
  // Paramètres (sous-onglets)
  'settings.seller-billing',
  'settings.email-smtp',
  'settings.email-templates',
  'settings.sms',
  'settings.sms-templates',
  'settings.google-calendar',
  'settings.crm-pipeline',
  'settings.site',
  'settings.categories',
  'settings.webinars',
  'settings.groups',
  'settings.webhooks',
  'settings.mcp',
  'settings.bi-studio',
  'settings.trash',
] as const;

export type LoTeamPermissionKey = (typeof LO_TEAM_PERMISSION_KEYS)[number];

export const LO_TEAM_PERMISSION_LABELS_FR: Record<LoTeamPermissionKey, string> = {
  'nav.dashboard': 'Accueil',
  'nav.learner': 'Mon apprentissage',
  'nav.analytics': 'Analytics',
  'nav.flows': 'Parcours EduFlow',
  'nav.supports': 'Supports',
  'nav.learners': 'Apprenants',
  'nav.sales': 'Ventes',
  'nav.marketing': 'Hub marketing',
  'nav.contacts': 'Contacts',
  'nav.email': 'Email marketing',
  'nav.automations': 'Automatisations',
  'nav.funnels': 'Tunnels',
  'nav.bundles': 'Packs formations',
  'nav.payments': 'Encaissement',
  'nav.site': 'Site web',
  'nav.blog': 'Blog',
  'nav.creatorPage': 'Page créateur',
  'nav.crm': 'CRM',
  'nav.affiliate': 'Affiliation',
  'nav.promotions': 'Coupons',
  'nav.booking': 'Calendrier / RDV',
  'nav.communities': 'Communautés',
  'nav.webinars': 'Webinaires',
  'nav.physical': 'Produits physiques',
  'nav.sms': 'SMS',
  'nav.videos': 'Vidéos',
  'nav.sounds': 'Sons',
  'nav.images': 'Images',
  'nav.documents': 'Documents',
  'nav.mediaDeposits': 'Dépôts',
  'nav.help': 'Aide',
  'nav.settings': 'Paramètres',
  'settings.seller-billing': 'Paramètres — Facturation & société',
  'settings.email-smtp': 'Paramètres — SMTP',
  'settings.email-templates': 'Paramètres — Templates email',
  'settings.sms': 'Paramètres — SMS',
  'settings.sms-templates': 'Paramètres — Templates SMS',
  'settings.google-calendar': 'Paramètres — Calendrier Google',
  'settings.crm-pipeline': 'Paramètres — Pipeline CRM',
  'settings.site': 'Paramètres — Site web',
  'settings.categories': 'Paramètres — Catégories',
  'settings.webinars': 'Paramètres — Webinaires',
  'settings.groups': 'Paramètres — Groupes',
  'settings.webhooks': 'Paramètres — Webhooks',
  'settings.mcp': 'Paramètres — MCP',
  'settings.bi-studio': 'Paramètres — Tableaux BI',
  'settings.trash': 'Paramètres — Corbeille',
};

export const LO_TEAM_PERMISSION_GROUPS: LoTeamPermissionGroup[] = [
  {
    id: 'pilotage',
    title: 'Pilotage',
    keys: ['nav.dashboard', 'nav.learner', 'nav.analytics'],
  },
  {
    id: 'activite',
    title: 'Activité',
    keys: ['nav.flows', 'nav.supports', 'nav.learners', 'nav.sales'],
  },
  {
    id: 'marketing',
    title: 'Marketing & vente',
    keys: [
      'nav.marketing', 'nav.contacts', 'nav.email', 'nav.automations', 'nav.funnels',
      'nav.bundles', 'nav.payments', 'nav.site', 'nav.blog', 'nav.creatorPage',
      'nav.crm', 'nav.affiliate', 'nav.promotions', 'nav.booking', 'nav.communities',
      'nav.webinars', 'nav.physical', 'nav.sms',
    ],
  },
  {
    id: 'medias',
    title: 'Médias',
    keys: ['nav.videos', 'nav.sounds', 'nav.images', 'nav.documents', 'nav.mediaDeposits'],
  },
  {
    id: 'general',
    title: 'Général',
    keys: ['nav.help', 'nav.settings'],
  },
  {
    id: 'settings',
    title: 'Accès paramètres (détail)',
    keys: [
      'settings.seller-billing', 'settings.email-smtp', 'settings.email-templates',
      'settings.sms', 'settings.sms-templates', 'settings.google-calendar', 'settings.crm-pipeline',
      'settings.site',
      'settings.categories', 'settings.webinars', 'settings.groups',
      'settings.webhooks', 'settings.mcp', 'settings.bi-studio', 'settings.trash',
    ],
  },
];

/** Permissions par défaut à l’invitation (accès large sauf paramètres sensibles). */
export function defaultLoTeamPermissions(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const key of LO_TEAM_PERMISSION_KEYS) {
    out[key] = !key.startsWith('settings.') && key !== 'nav.settings';
  }
  // Paramètres : off par défaut ; owner peut activer un par un
  for (const key of LO_TEAM_PERMISSION_KEYS) {
    if (key.startsWith('settings.')) out[key] = false;
  }
  return out;
}

export function normalizeLoTeamPermissions(raw: unknown): Record<string, boolean> {
  const base = defaultLoTeamPermissions();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const o = raw as Record<string, unknown>;
  for (const key of LO_TEAM_PERMISSION_KEYS) {
    if (o[key] === false) base[key] = false;
    else if (o[key] === true) base[key] = true;
  }
  return base;
}

export function isLoTeamPermissionAllowed(
  permissions: Record<string, boolean> | null | undefined,
  key: string,
  isOwner = false,
): boolean {
  if (isOwner) return true;
  return permissions?.[key] === true;
}

/** Mappe un chemin d’URL vers une clé de permission (accès direct). */
export function loTeamPermissionKeyForPath(pathname: string): string | null {
  const [pathPart, query = ''] = pathname.split('?');
  const path = (pathPart || '/').replace(/\/$/, '') || '/';
  const params = new URLSearchParams(query);
  const tab = params.get('tab');

  if (path === '/dashboard') {
    if (tab === 'flows') return 'nav.flows';
    if (tab === 'supports') return 'nav.supports';
    if (tab === 'learners') return 'nav.learners';
    if (tab === 'billing') return 'nav.sales';
    return 'nav.dashboard';
  }
  if (path === '/learner' || path.startsWith('/learner/')) return 'nav.learner';
  if (path === '/analytics' || path.startsWith('/analytics/')) return 'nav.analytics';
  if (path === '/funnels' || path.startsWith('/funnels/')) return 'nav.funnels';
  if (path === '/videos' || path.startsWith('/videos/')) return 'nav.videos';
  if (path === '/sounds' || path.startsWith('/sounds/')) return 'nav.sounds';
  if (path === '/images' || path.startsWith('/images/')) return 'nav.images';
  if (path === '/documents' || path.startsWith('/documents/')) return 'nav.documents';
  if (path === '/media/deposits' || path.startsWith('/media/deposits')) return 'nav.mediaDeposits';
  if (path === '/help' || path.startsWith('/help/')) return 'nav.help';

  if (path === '/marketing') return 'nav.marketing';
  if (path.startsWith('/marketing/contacts')) return 'nav.contacts';
  if (path.startsWith('/marketing/acquisition-forms')) return 'nav.contacts';
  if (path.startsWith('/marketing/email')) return 'nav.email';
  if (path.startsWith('/marketing/automations')) return 'nav.automations';
  if (path.startsWith('/marketing/formation-bundles')) return 'nav.bundles';
  if (path.startsWith('/marketing/payments')) return 'nav.payments';
  if (path.startsWith('/marketing/site/blog')) return 'nav.blog';
  if (path.startsWith('/marketing/site')) return 'nav.site';
  if (path.startsWith('/marketing/creator-page')) return 'nav.creatorPage';
  if (path.startsWith('/marketing/crm')) return 'nav.crm';
  if (path.startsWith('/marketing/affiliate')) return 'nav.affiliate';
  if (path.startsWith('/marketing/promotions')) return 'nav.promotions';
  if (path.startsWith('/marketing/booking')) return 'nav.booking';
  if (path.startsWith('/marketing/communities')) return 'nav.communities';
  if (path.startsWith('/marketing/webinars')) return 'nav.webinars';
  if (path.startsWith('/marketing/physical-products')) return 'nav.physical';
  if (path.startsWith('/marketing/sms')) return 'nav.sms';

  if (path === '/settings' || path.startsWith('/settings')) {
    if (tab) {
      const settingsKey = `settings.${tab}`;
      if ((LO_TEAM_PERMISSION_KEYS as readonly string[]).includes(settingsKey)) return settingsKey;
      const instMap: Record<string, string> = {
        categories: 'settings.categories',
        webinars: 'settings.webinars',
        groups: 'settings.groups',
        webhooks: 'settings.webhooks',
        mcp: 'settings.mcp',
        'bi-studio': 'settings.bi-studio',
        trash: 'settings.trash',
        team: 'nav.settings', // team tab = owner only, handled separately
      };
      if (instMap[tab]) return instMap[tab];
    }
    return 'nav.settings';
  }

  return null;
}

export function canAccessLoTeamPath(
  pathname: string,
  loTeam: { isOwner: boolean; menuPermissions: Record<string, boolean> | null } | null | undefined,
): boolean {
  if (!loTeam || loTeam.isOwner) return true;
  const key = loTeamPermissionKeyForPath(pathname);
  if (!key) return true; // chemins hors catalogue (ex. profil) autorisés
  const q = pathname.includes('?') ? pathname.slice(pathname.indexOf('?') + 1) : '';
  if (new URLSearchParams(q).get('tab') === 'team') return false; // équipe = owner uniquement
  return isLoTeamPermissionAllowed(loTeam.menuPermissions, key, false);
}

/** Première destination autorisée pour un membre (redirection accès refusé). */
export function firstAllowedLoTeamPath(
  loTeam: { isOwner: boolean; menuPermissions: Record<string, boolean> | null } | null | undefined,
): string {
  if (!loTeam || loTeam.isOwner) return '/dashboard';
  const candidates = [
    '/dashboard',
    '/learner',
    '/marketing',
    '/marketing/contacts',
    '/marketing/email',
    '/marketing/automations',
    '/marketing/site',
    '/marketing/crm',
    '/marketing/booking',
    '/marketing/sms',
    '/analytics',
    '/funnels',
    '/videos',
    '/help',
    '/settings',
  ];
  for (const path of candidates) {
    if (canAccessLoTeamPath(path, loTeam)) return path;
  }
  return '/profile';
}

export function isLoTeamAccountOwner(
  loTeam: { isOwner: boolean; memberId: string | null; mode?: 'own' | 'team' } | null | undefined,
): boolean {
  if (!loTeam) return true;
  if (loTeam.mode === 'team') return false;
  return loTeam.isOwner === true && !loTeam.memberId;
}
